/* eslint-disable prettier/prettier */
import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { travel, axiosConfig } from 'api';
import axios, { AxiosResponse } from 'axios';
import moment from 'moment';
import { Dispatch } from 'redux';
import { RootState } from 'store';

let cancel;
const CancelToken = axios.CancelToken;

import {
  flightsSearchRequest,
  flightsSearchSuccessful,
  flightsSearchError,
  flightResultsRequest,
  flightResultsSuccessful,
  flightResultsError,
  flightsClear,
  flightsTabFilterSuccessful,
  flightsExchangeRequest,
  flightsExchangeSuccessful,
  flightsExchangeError,
  flightsExchangeClear,
} from 'action-creators/flights';
import { TimeOption } from 'components/FlightsSearch/components/FlightTimePicker/models';
import { AdminUser } from 'models/adminUser';
import { Airport } from 'models/airport';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { FlightExchangeResponse } from 'models/flightExchange';
import { FlightFilter, FlightFilterResponse } from 'models/flightFilter';
import {
  FlightSearchRequest,
  FlightSearchResponse,
  FlightResultRequest,
  FlightResultResponse,
  Trip,
  FlightResultRequestOrderCriteriaEnum,
  ExchangeTrip,
  FlightExchangeRequest,
} from 'models/flightSearch';
import { FlightSolicitationRequest, AirBook, Traveler } from 'models/flightSolicitation';
import { FlightValidateResponse } from 'models/flightValidate';
import { FlightValidationResponse, FlightValidationRequest } from 'models/flightValidation';
import { Price } from 'models/price';
import { Response } from 'models/response';
import { Sector } from 'models/sector';
import { TravelRequested } from 'models/travelRequested';
import { amplitudelogEvent, TravelErrorEvent } from 'services/amplitudeService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount } from 'utils/generic';
import { convertTime } from 'utils/time';

import {
  flightsExchangeResultsError,
  flightsExchangeResultsRequest,
  flightsExchangeResultsSuccessful,
} from './../action-creators/flights';
import { ErrorPayload } from './../models/error';

type SearchFlightsArguments = {
  baggageIncluded: boolean;
  departurePoint: Airport;
  arrivalPoint: Airport;
  departureDate: Date;
  traveler?: TravelArrangerUsersAllowed;
  cabin: string;
  returnDate?: Date;
  newSessionToken?: string;
  departureTime?: TimeOption;
  returnTime?: TimeOption;
  directFlight: boolean;
  branchCompany?: GetAllBillingCompanyResponse;
};

export const searchFlights = ({
  baggageIncluded,
  departurePoint,
  arrivalPoint,
  departureDate,
  traveler,
  cabin,
  returnDate,
  newSessionToken,
  departureTime,
  returnTime,
  directFlight,
  branchCompany,
}: SearchFlightsArguments) => async (dispatch: Dispatch, getState: () => RootState) =>
  new Promise(async (resolve, reject) => {
    dispatch(
      flightsSearchRequest({
        baggageIncluded,
        departurePoint,
        arrivalPoint,
        departureDate,
        cabin,
        returnDate,
        traveler,
        departureTime,
        returnTime,
        directFlight,
        branchCompany,
      }),
    );

    const { accountResult } = getState().user;
    if (!accountResult) return;

    const { sessionToken, countryCurrency } = accountResult;

    try {
      const departureDateTime = departureTime
        ? `${moment(departureDate).format('YYYY-MM-DD')}T${convertTime(departureTime)}`
        : `${moment(departureDate).format('YYYY-MM-DD')}T00:00:01Z`;
      const trips: Trip[] = [
        new Trip(departurePoint.code, arrivalPoint.code, departureDateTime, cabin, baggageIncluded, directFlight),
      ];

      if (returnDate) {
        const returnDateTime = returnTime
          ? `${moment(returnDate).format('YYYY-MM-DD')}T${convertTime(returnTime)}`
          : `${moment(returnDate).format('YYYY-MM-DD')}T00:00:02Z`;
        trips.push(
          new Trip(arrivalPoint.code, departurePoint.code, returnDateTime, cabin, baggageIncluded, directFlight),
        );
      }

      const {
        data: { succeeded, data, errors },
      } = await travel.post<FlightSearchRequest, AxiosResponse<Response<FlightSearchResponse>>>('Air/AirSearchV2', {
        trips,
        currencyCode: countryCurrency,
        sessionToken: newSessionToken ? newSessionToken : sessionToken,
        isBusSearch: false,
        adults: 1,
        children: 0,
        infants: 0,
        maxResults: 0,
        billingCompanyId: branchCompany?.id,
      });

      if (!succeeded) {
        throw errors;
      }

      dispatch(flightsSearchSuccessful({ ...data, currency: countryCurrency }));
      resolve(data);
    } catch (ex) {
      const errors = (ex as ErrorPayload).response?.data?.Errors;

        const event: TravelErrorEvent = {
          SOURCE: 'TRAVEL_HOME',
          RESULTS: 0,
          TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
          TRAVEL_FROM: departurePoint?.code ?? '',
          TRAVEL_TO: arrivalPoint?.code ?? '',
          TRAVEL_DESTINATION_TYPE: '',
          TRAVEL_TRIP_TYPE: '',
          TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
          TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
          TRAVEL_DATE_TO: returnDate?.toString() ?? '',
          TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
          TRAVEL_PASSENGERS: '1',
          TRAVEL_ADULTS: '1',
          TRAVEL_CHILDREN: '0',
          TRAVEL_INFANTS: '0',
          TRAVEL_CLASS_OF_SERVICE: '',
          TRAVEL_ROOMS: '1',
          TRAVEL_WITH_BAGGAGE: '',
          TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
          SEARCHID: '',
          ERROR_MESSAGE: errors?.join('') ?? '',
        };
        amplitudelogEvent(amplitudeEvents.TRAVEL_SEARCH_ERROR, event, accountResult);

      if (errors?.some((e: string) => e.toLowerCase().includes('no results'))) {
        const emptyData: FlightSearchResponse = {
          results: [],
          searchId: '',
          distanceRestriction: 'Domestic',
          resultsAvailableUntil: new Date(),
          resultsCount: 0,
        };
        dispatch(flightsSearchSuccessful({ ...emptyData, currency: countryCurrency }));
        resolve(emptyData);
        return;
      }

      Sentry.addBreadcrumb({
        category: 'flight',
        message: 'Could not search flights',
        data: { ex, response: (ex as ErrorPayload).response },
      });
      Sentry.captureMessage('[Flights] Could not search flights');
      dispatch(flightsSearchError(String(ex)));
      reject(ex);
    }
  });

export const flightResults = (
  currentOrderCriteria: FlightResultRequestOrderCriteriaEnum,
  newFilters?: FlightFilter,
) => async (dispatch: Dispatch, getState: () => RootState) => {
  try {
    const { accountResult } = getState().user;
    if (!accountResult) return;
    const { sessionToken, id: loggedUserId } = accountResult;

    const { orderCriteria, filters, search, isEnd, isLoadingResults, traveler } = getState().flights;
    if (!search) return;
    const { results, searchId, resultsCount, distanceRestriction, lowestLogicalFare } = search;

    const maxResultsPerPage = Number(ENV.SEARCH_MAX_RESULT_PER_PAGE);

    if (
      (isEnd && (newFilters === undefined || newFilters === null) && orderCriteria === currentOrderCriteria) ||
      ((newFilters === undefined || newFilters === null) &&
        results.length >= resultsCount &&
        orderCriteria === currentOrderCriteria)
    ) {
      return;
    }

    if (isLoadingResults && (newFilters === undefined || newFilters === null)) return;

    if (isLoadingResults && cancel) cancel();

    dispatch(flightResultsRequest(currentOrderCriteria, newFilters));
    const {
      data: { data },
    } = await travel.post<FlightResultRequest, AxiosResponse<Response<FlightResultResponse[]>>>(
      'Air/AirGroupedResults',
      {
        userId: traveler ? traveler.id : loggedUserId,
        travelArrangerUserId: traveler && traveler.id !== loggedUserId ? loggedUserId : undefined,
        searchId,
        resultCountLowerBound: !newFilters && orderCriteria === currentOrderCriteria ? results.length : 0,
        resultCountUpperBound:
          !newFilters && orderCriteria === currentOrderCriteria
            ? results.length + maxResultsPerPage
            : maxResultsPerPage,
        orderCriteria: currentOrderCriteria,
        distanceRestriction,
        lowestLogicalFare,
        sessionToken,
        orderDesc: false,
        ...(newFilters || filters),
      } as FlightResultRequest,
      {
        cancelToken: new CancelToken((c) => {
          cancel = c;
        }),
      },
    );

    dispatch(flightResultsSuccessful(data));
  } catch (ex) {
    Sentry.addBreadcrumb({
      category: 'flight',
      message: 'Could not get flight results',
      data: { ex, response: (ex as ErrorPayload).response },
    });
    Sentry.captureMessage('[Flights] Could not get flight results');
    dispatch(flightResultsError(String(ex)));
  }
};

export const confirmFlightSolicitation = (
  flight: FlightValidationResponse,
  justifyForBreakingRules: string,
  reason: string,
  validateResponse: FlightValidateResponse,
  upSellId?: string,
  upSellOptionId?: number,
  costCenterId?: string,
  breakRulesMessageId?: string,
) => (dispatch: Dispatch, getState: () => RootState) =>
  new Promise<void>(async (resolve, reject) => {
    const { accountResult } = getState().user;
    const {
      departureDate,
      search,
      traveler,
      returnDate,
      departurePoint,
      arrivalPoint,
      branchCompany,
    } = getState().flights;
    try {
      const { currency } = getState().i18n.country;

      if (!currency || !departureDate || !search || !accountResult) return;

      let airBookTraveler: Traveler = {
        gender: accountResult.gender,
        firstName: accountResult.name,
        lastName: accountResult.lastName,
        phone: accountResult.phoneNumber,
        email: accountResult.email,
        nationality: accountResult.country,
        dob: new Date(accountResult.birthDate),
        documentNumber: accountResult.documentNumber,
        passportNumber: accountResult.passportNumber,
        paxType: 'Adult',
      };

      if (traveler) {
        airBookTraveler = {
          gender: traveler.gender,
          firstName: traveler.name,
          lastName: traveler.lastName,
          phone: traveler.phoneNumber,
          email: traveler.email,
          nationality: traveler.country,
          dob: new Date(traveler.birthDate),
          documentNumber: traveler.documentNumber,
          passportNumber: traveler.passportNumber,
          paxType: 'Adult',
        };
      }

      const airBook: AirBook = {
        sessionToken: accountResult.sessionToken,
        searchid: search.searchId,
        optionid: flight.tripOption.optionId,
        currency,
        travelDate: departureDate,
        distanceRestriction: search.distanceRestriction,
        sectors: flight.tripOption.sectors,
        price: flight.tripOption.price,
        travelers: [airBookTraveler],
        lowestLogicalFare: search.lowestLogicalFare,
        upSellId: upSellId && upSellId !== '' ? upSellId : undefined,
        upSellOptionId: upSellOptionId && upSellOptionId !== 0 ? upSellOptionId : undefined,
      };

      await travel.post('Travels/Create', {
        userId: traveler ? traveler.id || traveler.userId : accountResult.id,
        userFullName: traveler
          ? `${traveler.name} ${traveler.lastName}`
          : `${accountResult.name} ${accountResult.lastName}`,
        companyId: accountResult.companyId,
        currency,
        airBook,
        justifyForBreakingRules,
        reason,
        costCenterId,
        travelArrangerUserId: traveler && traveler.id !== accountResult.id ? accountResult.id : undefined,
        validationId: validateResponse.validationId,
        documentId: validateResponse.documentId,
        breakRuleReasonId: breakRulesMessageId,
        billingCompanyId: branchCompany?.id,
        lowestLogicalFareList: search.lowestLogicalFareList,
      } as FlightSolicitationRequest);

        dispatch(flightsClear());
        resolve();
      } catch (ex) {
        const event: TravelErrorEvent = {
          SOURCE: 'TRAVEL_HOME',
          RESULTS: 0,
          TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
          TRAVEL_FROM: departurePoint?.code ?? '',
          TRAVEL_TO: arrivalPoint?.code ?? '',
          TRAVEL_DESTINATION_TYPE: search?.distanceRestriction ?? '',
          TRAVEL_TRIP_TYPE: !(departurePoint && !returnDate) ? 'true' : 'false',
          TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
          TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
          TRAVEL_DATE_TO: returnDate?.toString() ?? '',
          TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
          TRAVEL_PASSENGERS: '1',
          TRAVEL_ADULTS: '1',
          TRAVEL_CHILDREN: '0',
          TRAVEL_INFANTS: '0',
          TRAVEL_CLASS_OF_SERVICE: '',
          TRAVEL_ROOMS: '1',
          TRAVEL_WITH_BAGGAGE: '',
          TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
          SEARCHID: search?.searchId ?? '',
          ERROR_MESSAGE: ex?.toString() ?? '',
        };
        await amplitudelogEvent(amplitudeEvents.TRAVEL_BOOKING_ERROR, event, accountResult);

      reject(ex);
    }
  });

export const getFilters = () => async (dispatch: Dispatch, getState: () => RootState) =>
  new Promise<FlightFilterResponse>(async (resolve, reject) => {
    try {
      const { accountResult } = getState().user;
      const { search } = getState().flights;

      const {
        data: { data },
      } = await travel.get<Response<FlightFilterResponse>>('Air/AirFilter', {
        params: {
          sessionToken: accountResult?.sessionToken || '',
          searchId: search?.searchId,
        },
        ...axiosConfig,
      });
      const { airFilterTabs } = data;

      dispatch(flightsTabFilterSuccessful({ airFilterTabs }));

      resolve(data);
    } catch (ex) {
      Sentry.addBreadcrumb({
        category: 'flight',
        message: 'Could not get flight filters',
        data: { ex, response: (ex as ErrorPayload).response },
      });
      Sentry.captureMessage('[Flights] Could not get flight filters');
      reject(ex);
    }
  });

export const fetchFlightDetails = async (
  searchId: string,
  sessionToken: string,
  optionId: number,
  userId: string,
  distanceRestriction: string,
  lowestLogicalFare: number,
  upSellId?: string,
  upSellOptionId = 0,
) =>
  travel.post<FlightValidationRequest, AxiosResponse<Response<FlightValidationResponse>>>('Air/AirValidation', {
    searchId,
    sessionToken,
    optionId,
    upSellOptionId,
    upSellId,
    userId,
    distanceRestriction,
    lowestLogicalFare,
  });

export const getAirOptionIdFromGroupedResults = ([departureFlightKey, returnFlightKey]: [string, string]) => async (
  _: Dispatch,
  getState: () => RootState,
) =>
  new Promise<{ optionId: number }>(async (resolve, reject) => {
    try {
      const { accountResult } = getState().user;
      const { search } = getState().flights;
      const {
        data: { data },
      } = await travel.get<Response<{ optionId: number }>>('Air/AirOptionIdFromGroupedResult', {
        params: {
          sessionToken: accountResult?.sessionToken || '',
          searchId: search?.searchId,
          flightKeys: `${departureFlightKey}${returnFlightKey !== undefined ? ',' + returnFlightKey : ''}`,
        },
      });

      resolve(data);
    } catch (error) {
      Sentry.captureMessage(
        `[Flights] Could not get AirOptionId from flights: ${departureFlightKey}, ${returnFlightKey}`,
      );
      reject(error);
    }
  });

export const validateFlight = (
  flight: FlightValidationResponse,
  justifyForBreakingRules: string,
  reason: string,
  redux: RootState,
  upSellId?: string,
  upSellOptionId?: number,
  costCenterId?: string,
) =>
  new Promise<any>(async (resolve, reject) => {
    const { accountResult } = redux.user;
    const { departureDate, search, traveler, returnDate, departurePoint, arrivalPoint, branchCompany } = redux.flights;

    if (!accountResult || !search) {
      return;
    }

    try {
      const { currency } = redux.i18n.country;

      if (!currency || !departureDate || !search || !accountResult) return;

      let airBookTraveler: Traveler = {
        gender: accountResult.gender,
        firstName: accountResult.name,
        lastName: accountResult.lastName,
        phone: accountResult.phoneNumber,
        email: accountResult.email,
        nationality: accountResult.country,
        dob: new Date(accountResult.birthDate),
        documentNumber: accountResult.documentNumber,
        passportNumber: accountResult.passportNumber,
        paxType: 'Adult',
      };

      if (traveler) {
        airBookTraveler = {
          gender: traveler.gender,
          firstName: traveler.name,
          lastName: traveler.lastName,
          phone: traveler.phoneNumber,
          email: traveler.email,
          nationality: traveler.country,
          dob: new Date(traveler.birthDate),
          documentNumber: traveler.documentNumber,
          passportNumber: traveler.passportNumber,
          paxType: 'Adult',
        };
      }

      const airBook: AirBook = {
        sessionToken: accountResult.sessionToken,
        searchid: search.searchId,
        optionid: flight.tripOption.optionId,
        currency,
        travelDate: departureDate,
        distanceRestriction: search.distanceRestriction,
        sectors: flight.tripOption.sectors,
        price: flight.tripOption.price,
        travelers: [airBookTraveler],
        lowestLogicalFare: search.lowestLogicalFare,
        upSellId: upSellId && upSellId !== '' ? upSellId : undefined,
        upSellOptionId: upSellOptionId && upSellOptionId !== 0 ? upSellOptionId : undefined,
      };

      const data = await travel
        .post<FlightValidationResponse>('Travels/Validate', {
          userId: traveler ? traveler.id || traveler.userId : accountResult.id,
          userFullName: traveler
            ? `${traveler.name} ${traveler.lastName}`
            : `${accountResult.name} ${accountResult.lastName}`,
          companyId: accountResult.companyId,
          currency,
          airBook,
          justifyForBreakingRules,
          reason,
          costCenterId,
          travelArrangerUserId: traveler && traveler.id !== accountResult.id ? accountResult.id : undefined,
          billingCompanyId: branchCompany?.id,
          lowestLogicalFareList: search.lowestLogicalFareList,
        } as FlightSolicitationRequest)
        .then((response) => response.data);

    resolve(data);
  } catch (ex) {
    const event: TravelErrorEvent = {
      SOURCE: 'TRAVEL_HOME',
      RESULTS: 0,
      TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
      TRAVEL_FROM: departurePoint?.code ?? '',
      TRAVEL_TO: arrivalPoint?.code ?? '',
      TRAVEL_DESTINATION_TYPE: search?.distanceRestriction ?? '',
      TRAVEL_TRIP_TYPE: !(departurePoint && !returnDate) ? 'true' : 'false',
      TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
      TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
      TRAVEL_DATE_TO: returnDate?.toString() ?? '',
      TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
      TRAVEL_PASSENGERS: '1',
      TRAVEL_ADULTS: '1',
      TRAVEL_CHILDREN: '0',
      TRAVEL_INFANTS: '0',
      TRAVEL_CLASS_OF_SERVICE: '',
      TRAVEL_ROOMS: '1',
      TRAVEL_WITH_BAGGAGE: '',
      TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
      SEARCHID: search?.searchId ?? '',
      ERROR_MESSAGE: ex?.toString() ?? '',
    };
    await amplitudelogEvent(amplitudeEvents.TRAVEL_BOOKING_ERROR, event, accountResult);

      reject(ex);
    }
  });

type SearchExchangeArguments = {
  departureCode?: string;
  departureDate?: Date;
  arrivalCode?: string;
  arrivalDate?: Date;
  changeDeparture?: boolean;
  changeArrival?: boolean;
  newSessionToken?: string;
  originalFlight?: TravelRequested;
};

export const searchExchangeFlights = ({
  departureCode,
  departureDate,
  arrivalCode,
  arrivalDate,
  changeDeparture,
  changeArrival,
  newSessionToken,
  originalFlight,
}: SearchExchangeArguments) => async (dispatch: Dispatch, getState: () => RootState) =>
  new Promise(async (resolve, reject) => {
    const { accountResult } = getState().user;
    if (!accountResult) return;
    const { sessionToken } = accountResult;

    dispatch(
      flightsExchangeRequest({
        departureCode,
        departureDate,
        arrivalCode,
        arrivalDate,
        originalFlight,
      }),
    );

    try {
      const trips: ExchangeTrip[] = [
        {
          departurePoint: departureCode,
          arrivalPoint: arrivalCode,
          sectorNumber: 1,
          departureDate: `${moment(departureDate).format('YYYY-MM-DD')}`,
          sectorChange: changeDeparture,
        } as ExchangeTrip,
      ];

      if (arrivalDate) {
        const arrivalDateTime = `${moment(arrivalDate).format('YYYY-MM-DD')}T00:00:02Z`;
        trips.push({
          departurePoint: arrivalCode,
          arrivalPoint: departureCode,
          departureDateWithOutFormat: arrivalDateTime,
          sectorNumber: 2,
          departureDate: `${moment(arrivalDate).format('YYYY-MM-DD')}`,
          sectorChange: changeArrival,
          reservationIndex: 0,
        } as ExchangeTrip);
      }
      const { changeFlight } = getState().flights;
      const { originalFlight } = changeFlight;

      const payload: FlightExchangeRequest = {
        trips,
        currencyCode: originalFlight?.currencyCode || 'COP',
        isBusSearch: false,
        adults: 1,
        children: 0,
        infants: 0,
        maxResults: 0,
        billingCompanyId: originalFlight?.billingCompanyId || '',
        travelId: originalFlight?.id || '',
        sessionToken: newSessionToken ? newSessionToken : sessionToken,
        userId: originalFlight?.userId || '',
      };
      const {
        data: { succeeded, data, errors },
      } = await travel.post<FlightExchangeRequest, AxiosResponse<Response<FlightExchangeResponse>>>(
        'Air/AirSearchExchange',
        payload,
        {
          cancelToken: new CancelToken((c) => {
            cancel = c;
          }),
        },
      );

      if (!succeeded) {
        dispatch(flightsExchangeError(String(errors)));
        reject(errors);
      }

      dispatch(flightsExchangeSuccessful({ ...data, results: [], resultsCount: data.results.length }));
      resolve({ ...data, results: [], resultsCount: data.results.length });
    } catch (ex) {
      const errors = (ex as ErrorPayload)?.response?.data?.Errors;

      /*  const event: TravelErrorEvent = {
        SOURCE: 'TRAVEL_HOME',
        RESULTS: 0,
        TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
        TRAVEL_FROM: departurePoint?.code ?? '',
        TRAVEL_TO: arrivalPoint?.code ?? '',
        TRAVEL_DESTINATION_TYPE: '',
        TRAVEL_TRIP_TYPE: '',
        TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
        TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
        TRAVEL_DATE_TO: returnDate?.toString() ?? '',
        TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
        TRAVEL_PASSENGERS: '1',
        TRAVEL_ADULTS: '1',
        TRAVEL_CHILDREN: '0',
        TRAVEL_INFANTS: '0',
        TRAVEL_CLASS_OF_SERVICE: '',
        TRAVEL_ROOMS: '1',
        TRAVEL_WITH_BAGGAGE: '',
        TRAVEL_BRANCH_CODE: process.env.REACT_APP_RAPPI_BRANCH_CODE ?? '',
        SEARCHID: '',
        ERROR_MESSAGE: errors?.join(''),
      };
      amplitudelogEvent(amplitudeEvents.TRAVEL_SEARCH_ERROR, event, accountResult); */

      if (errors?.some((e: string) => e.toLowerCase().includes('no results'))) {
        const emptyData: FlightExchangeResponse = {
          results: [],
          searchId: '',
          distanceRestriction: 'Domestic',
          resultsAvailableUntil: new Date(),
          resultsCount: 0,
        };
        dispatch(flightsExchangeSuccessful(emptyData));
        resolve(emptyData);
        return;
      }

      /* Sentry.addBreadcrumb({
        category: 'flight',
        message: 'Could not search flights',
        data: { ex, response: ex.response },
      });
      Sentry.captureMessage('[Flights] Could not search flights'); */
      dispatch(flightsExchangeError(String(ex)));
      reject(ex);
    }
  });

export const confirmFlightExchange = (
  sectors: Sector[],
  price: Price,
  optionId: number,
  traveler: AdminUser,
  validateResponse: FlightValidateResponse,
  justifyForBreakingRules: string,
  reason: string,
  breakRulesMessageId?: string,
) => (dispatch: Dispatch, getState: () => RootState) =>
  new Promise<void>(async (resolve, reject) => {
    const { accountResult } = getState().user;
    const { changeFlight, changeFlightSearch: search } = getState().flights;
    const { originalFlight, departureDate, departureCode, arrivalCode, arrivalDate } = changeFlight;

    if (
      !search ||
      !changeFlight ||
      !accountResult ||
      !originalFlight ||
      !departureCode ||
      !departureDate ||
      !arrivalCode ||
      !arrivalDate
    )
      return;

    try {
      const airBookTraveler: Traveler = {
        gender: traveler.gender,
        firstName: traveler.name,
        lastName: traveler.lastName,
        phone: traveler.phoneNumber,
        email: traveler.email ?? '',
        nationality: traveler.country ?? '',
        dob: new Date(traveler.birthDate),
        documentNumber: traveler.documentNumber,
        passportNumber: traveler.passportNumber,
        paxType: 'Adult',
      };

      const airBook: AirBook = {
        sessionToken: accountResult.sessionToken,
        searchid: search.searchId,
        optionid: optionId,
        currency: originalFlight.currencyCode,
        travelDate: departureDate,
        distanceRestriction: search.distanceRestriction,
        sectors,
        price,
        travelers: [airBookTraveler],
        lowestLogicalFare: search.lowestLogicalFare,
        upSellId: undefined,
        upSellOptionId: 0,
      };

      const payload: FlightSolicitationRequest = {
        userId: originalFlight.userId ?? '',
        userFullName: `${traveler.name} ${traveler.lastName}`,
        currency: originalFlight.currencyCode,
        companyId: originalFlight.companyId || '',
        airBook,
        justifyForBreakingRules,
        reason,
        costCenterId: originalFlight.costCenterId ?? '',
        travelArrangerUserId: traveler && traveler.id !== accountResult.id ? accountResult.id : undefined,
        validationId: validateResponse.validationId,
        documentId: validateResponse.documentId,
        billingCompanyId: originalFlight.billingCompanyId ?? '',
        lowestLogicalFareList: search.lowestLogicalFareList ?? [],
        breakRuleReasonId: breakRulesMessageId ?? '',
        TravelIdFlightExchanged: originalFlight.id,
      };
      const { data } = await travel.post('Travels/Create', payload);

      dispatch(flightsExchangeClear());

      resolve(data);
    } catch (ex) {
      /* const event: TravelErrorEvent = {
        SOURCE: 'TRAVEL_HOME',
        RESULTS: 0,
        TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
        TRAVEL_FROM: departurePoint?.code ?? '',
        TRAVEL_TO: arrivalPoint?.code ?? '',
        TRAVEL_DESTINATION_TYPE: search?.distanceRestriction ?? '',
        TRAVEL_TRIP_TYPE: !(departurePoint && !returnDate) ? 'true' : 'false',
        TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
        TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
        TRAVEL_DATE_TO: returnDate?.toString() ?? '',
        TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
        TRAVEL_PASSENGERS: '1',
        TRAVEL_ADULTS: '1',
        TRAVEL_CHILDREN: '0',
        TRAVEL_INFANTS: '0',
        TRAVEL_CLASS_OF_SERVICE: '',
        TRAVEL_ROOMS: '1',
        TRAVEL_WITH_BAGGAGE: '',
        TRAVEL_BRANCH_CODE: process.env.REACT_APP_RAPPI_BRANCH_CODE ?? '',
        SEARCHID: search?.searchId ?? '',
        ERROR_MESSAGE: ex.toString(),
      };
      await amplitudelogEvent(amplitudeEvents.TRAVEL_BOOKING_ERROR, event, accountResult); */

      reject(ex);
    }
  });

export const flightExchangeResults = (
  currentOrderCriteria: FlightResultRequestOrderCriteriaEnum,
  newFilters?: FlightFilter,
) => async (dispatch: Dispatch, getState: () => RootState) => {
  try {
    const { accountResult } = getState().user;
    if (!accountResult) return;
    const { sessionToken } = accountResult;

    const { changeFlightStatus, changeFlight } = getState().flights;
    const { originalFlight } = changeFlight;
    const { orderCriteria, filters, isEnd, isLoadingResults } = changeFlightStatus;
    const { changeFlightSearch: search } = getState().flights;

    if (!search) return;
    const { results, searchId, resultsCount, distanceRestriction, lowestLogicalFare } = search;

    const maxResultsPerPage = Number(process.env.REACT_APP_SEARCH_MAX_RESULT_PER_PAGE);

    if (
      (isEnd && (newFilters === undefined || newFilters === null) && orderCriteria === currentOrderCriteria) ||
      ((newFilters === undefined || newFilters === null) &&
        results.length >= resultsCount &&
        orderCriteria === currentOrderCriteria)
    ) {
      return;
    }

    if (isLoadingResults && (newFilters === undefined || newFilters === null)) return;

    if (isLoadingResults && cancel) cancel();

    const payload: FlightResultRequest = {
      userId: originalFlight?.userId || '',
      searchId,
      resultCountLowerBound: !newFilters && orderCriteria === currentOrderCriteria ? results.length : 0,
      resultCountUpperBound:
        !newFilters && orderCriteria === currentOrderCriteria ? results.length + maxResultsPerPage : maxResultsPerPage,
      orderCriteria: currentOrderCriteria,
      distanceRestriction: distanceRestriction || 'Domestic',
      lowestLogicalFare: lowestLogicalFare || 0,
      sessionToken,
      orderDesc: false,
      ...(newFilters || filters),
    };
    dispatch(flightsExchangeResultsRequest(currentOrderCriteria, newFilters));
    const {
      data: { data },
    } = await travel.post<FlightResultRequest, AxiosResponse<Response<FlightResultResponse[]>>>(
      'Air/AirResults',
      payload,
      {
        cancelToken: new CancelToken((c) => {
          cancel = c;
        }),
      },
    );

    dispatch(flightsExchangeResultsSuccessful(data));
  } catch (ex) {
    /* Sentry.addBreadcrumb({
      category: 'flight',
      message: 'Could not get flight results',
      data: { ex, response: (ex as ErrorPayload).response },
    });
    Sentry.captureMessage('[Flights] Could not get flight results'); */
    dispatch(flightsExchangeResultsError(String(ex)));
  }
};

export const validateExchangeFlight = (
  traveler: AdminUser,
  sectors: Sector[],
  price: Price,
  optionId: number,
  justifyForBreakingRules: string,
  reason: string,
  redux: RootState,
) =>
  new Promise<any>(async (resolve, reject) => {
    const { accountResult } = redux.user;
    const { changeFlight, changeFlightSearch: search } = redux.flights;
    const { departureDate, arrivalDate, departureCode, arrivalCode, originalFlight } = changeFlight ?? {
      departureDate: undefined,
      arrivalDate: undefined,
      departureCode: '',
      arrivalCode: '',
      originalFlight: undefined,
    };

    if (originalFlight === undefined || originalFlight.userId === undefined) return;
    if (!accountResult || !search) {
      return;
    }
    if (departureDate === undefined || arrivalDate === undefined || departureCode === '' || arrivalCode === '') return;
    const currency = changeFlight.originalFlight?.currencyCode;

    try {
      const airBookTraveler: Traveler = {
        gender: traveler.gender,
        firstName: traveler.name,
        lastName: traveler.lastName,
        phone: traveler.phoneNumber,
        email: traveler.email || '',
        nationality: traveler.country || '',
        dob: new Date(traveler.birthDate),
        documentNumber: traveler.documentNumber,
        passportNumber: traveler.passportNumber,
        paxType: 'Adult',
      };

      const airBook: AirBook = {
        sessionToken: accountResult.sessionToken,
        searchid: search.searchId,
        optionid: optionId,
        currency: currency ?? 'COP',
        travelDate: departureDate,
        distanceRestriction: search.distanceRestriction,
        sectors,
        price,
        travelers: [airBookTraveler],
        lowestLogicalFare: search.lowestLogicalFare,
        upSellId: undefined,
        upSellOptionId: 0,
      };

      const payload: FlightSolicitationRequest = {
        userId: originalFlight.userId,
        userFullName: `${traveler.name} ${traveler.lastName}`,
        companyId: traveler.companyId ?? '',
        currency: currency ?? 'COP',
        airBook,
        justifyForBreakingRules,
        reason,
        costCenterId: originalFlight.costCenterId ?? '',
        travelArrangerUserId: traveler && traveler.id !== accountResult.id ? accountResult.id : undefined,
        billingCompanyId: originalFlight.billingCompanyId ?? '',
        lowestLogicalFareList: search.lowestLogicalFareList ?? [],
      };

      const data = await travel
        .post<FlightValidationResponse>('Travels/Validate', payload)
        .then((response) => response.data);

      resolve(data);
    } catch (ex) {
      //   const event: TravelErrorEvent = {
      //     SOURCE: 'TRAVEL_HOME',
      //     RESULTS: 0,
      //     TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
      //     TRAVEL_FROM: departurePoint?.code ?? '',
      //     TRAVEL_TO: arrivalPoint?.code ?? '',
      //     TRAVEL_DESTINATION_TYPE: search?.distanceRestriction ?? '',
      //     TRAVEL_TRIP_TYPE: !(departurePoint && !returnDate) ? 'true' : 'false',
      //     TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
      //     TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
      //     TRAVEL_DATE_TO: returnDate?.toString() ?? '',
      //     TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
      //     TRAVEL_PASSENGERS: '1',
      //     TRAVEL_ADULTS: '1',
      //     TRAVEL_CHILDREN: '0',
      //     TRAVEL_INFANTS: '0',
      //     TRAVEL_CLASS_OF_SERVICE: '',
      //     TRAVEL_ROOMS: '1',
      //     TRAVEL_WITH_BAGGAGE: '',
      //     TRAVEL_BRANCH_CODE: process.env.REACT_APP_RAPPI_BRANCH_CODE ?? '',
      //     SEARCHID: search?.searchId ?? '',
      //     ERROR_MESSAGE: ex.toString(),
      //   };
      //   await amplitudelogEvent(amplitudeEvents.TRAVEL_BOOKING_ERROR, event, accountResult);

      reject(ex);
    }
  });
